import React from "react";
import { handoffLabel, languageLabel } from "../AiAssistant/aiHelpers";

// Shown above the reply box of a chat when the AI Assistant is involved: who has the chat, why it was handed
// over (with the assistant's own summary for the team), and, in shadow mode, the reply it suggests.
// Renders nothing when the assistant has nothing to say about this chat.
//
// `info`           what GET ai/chat_info returned (see useAiChatInfo), or null
// `canReply`       whether this user may send in this chat (an employee only on a chat assigned to them)
// `loadedDraftId`  the draft already copied into the reply box, if any
const AiChatPanel = ({ info, canReply, loadedDraftId, onUseDraft, onDiscardDraft, onReturnToAi, busy }) => {
  if (!info) return null;
  const { mode, ai_state: state, handoff, draft, language, handoff_reason: reason } = info;
  const handedOver = state === "human";
  if (mode === "off" && !draft && !(handedOver && handoff)) return null;

  const lang = languageLabel(language);
  const answering = state === "ai" && mode === "live";
  const drafting = state === "ai" && mode === "shadow";

  return (
    <div style={{ borderBottom: "1px solid #eef0f2", background: "#fafbff", padding: "8px 14px", fontSize: 12.5 }} data-testid="ai-chat-panel">
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <span style={{ fontWeight: 700, color: "#3b5bdb" }}>AI Assistant</span>
        {answering && <span className="text-muted">is answering this chat</span>}
        {drafting && <span className="text-muted">is suggesting replies (nothing is sent to the customer)</span>}
        {handedOver && <span className="text-muted">handed this chat to your team{reason ? `: ${handoffLabel(reason)}` : ""}</span>}
        {lang && (
          <span className="badge text-bg-light border" style={{ fontSize: 10 }}>
            {lang}
          </span>
        )}
        {handedOver && mode !== "off" && (
          <button type="button" className="btn btn-sm btn-outline-primary ms-auto py-0" style={{ fontSize: 11 }} onClick={onReturnToAi} disabled={busy}>
            Return to AI
          </button>
        )}
      </div>

      {handedOver && handoff && (
        <div className="mt-2 p-2 rounded-2" style={{ background: "#fff", border: "1px solid #e3e7ff" }}>
          <div className="d-flex align-items-center gap-2 mb-1">
            <strong>For the team</strong>
            {handoff.priority === "high" && (
              <span className="badge text-bg-danger" style={{ fontSize: 10 }}>
                High priority
              </span>
            )}
          </div>
          <div style={{ whiteSpace: "pre-wrap" }}>{handoff.summary}</div>
        </div>
      )}

      {draft && (
        <div className="mt-2 p-2 rounded-2" style={{ background: "#fff8e1", border: "1px solid #ffe29a" }}>
          <div className="d-flex align-items-center gap-2 mb-1">
            <strong>Suggested reply</strong>
            <span className="text-muted">only you can see this</span>
          </div>
          <div style={{ whiteSpace: "pre-wrap" }}>{draft.text}</div>
          {draft.meta && draft.meta.handoff && (
            <div className="text-muted mt-1">The assistant would also have handed this chat to your team ({handoffLabel(draft.meta.handoff.reason)}).</div>
          )}
          {draft.meta && draft.meta.callback && <div className="text-muted mt-1">The assistant would also have booked a callback.</div>}
          <div className="d-flex gap-2 mt-2">
            {loadedDraftId === draft.id ? (
              <span className="text-muted">Copied to the reply box: edit it if you like, then send.</span>
            ) : (
              <button
                type="button"
                className="btn btn-sm btn-warning py-0"
                style={{ fontSize: 11 }}
                disabled={!canReply || busy}
                title={canReply ? "Copy it into the reply box" : "Assign the chat to yourself to reply"}
                onClick={() => onUseDraft(draft)}
              >
                Use this reply
              </button>
            )}
            <button type="button" className="btn btn-sm btn-outline-secondary py-0" style={{ fontSize: 11 }} disabled={busy} onClick={() => onDiscardDraft(draft)}>
              Discard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiChatPanel;
